/**
 * Typed fetch client for /v1/dashboard/* endpoints.
 *
 * Hand-mirrored against `dashboard_api/schema.py` per the dashboard
 * spec ("no codegen required at Phase 1; hand-write the types"). Each
 * Pydantic response model has a matching TypeScript interface here;
 * shapes that the spec leaves opaque (`details`, `external_data`,
 * arbitrary request/response/error bags) use `Record<string, unknown>`,
 * mirroring the `dict[str, Any]` declarations on the Python side.
 *
 * Auth: login happens via a real browser redirect to Google
 * (`/v1/dashboard/auth/login`, see `pages/LoginPage.tsx`), not through
 * this module — OAuth's redirect chain can't be driven by `fetch`. Once
 * signed in, the browser holds an `HttpOnly` session cookie and every
 * request here sends it automatically via `credentials: "same-origin"`
 * (no `Authorization` header built here). A 401 means no/expired
 * session; `main.tsx`'s `QueryCache.onError` redirects to `/login` when
 * that happens. Dev mode relies on Vite's proxy to forward to the
 * backend on localhost:8000.
 *
 * Errors: non-2xx responses raise `ApiError` carrying the status and
 * the response body (parsed as JSON when possible). The pages catch
 * this to render error states; TanStack Query exposes it as the
 * hook's `error`.
 */

// ---------------------------------------------------------------------------
// Severity + inquiry-status literals
// ---------------------------------------------------------------------------

export type Severity = "INFO" | "AGENT" | "ERROR" | "CRITICAL";
export type InquiryStatus = "in_flight" | "stuck" | "finalized" | "partial";

export const SEVERITY_VALUES: readonly Severity[] = [
  "INFO",
  "AGENT",
  "ERROR",
  "CRITICAL",
] as const;

// ---------------------------------------------------------------------------
// Response shapes (mirror dashboard_api/schema.py)
// ---------------------------------------------------------------------------

/** Per the ingestion spec — known shape of the `config_snapshot` JSONB. */
export interface ConfigSnapshot {
  pinewood_enabled: boolean;
  elms_enabled: boolean;
  emilfrey_enabled: boolean;
  dealerweb_enabled: boolean;
  elead_enabled: boolean;
  vinsolutions_enabled: boolean;
  contact_points: string[];
}

export interface DetectionEventResponse {
  id: number;
  rule_id: string;
  severity: Severity;
  /** Integer; mirrors public.inquiry.id. Null for meta events
   * (`meta_rule_failure`, etc.). */
  inquiry_id: number | null;
  /** ISO-8601 timestamp string. */
  detected_at: string;
  message: string;
  details: Record<string, unknown>;
}

export interface DetectionsListResponse {
  events: DetectionEventResponse[];
  next_cursor: string | null;
}

export interface InquiryCreatedResponse {
  /** ISO-8601 timestamp string. */
  captured_at: string;
  /** Integer or null; mirrors public.inquiry.dealershipid. */
  dealership_id: number | null;
  chat_reference: string | null;
  /** Integer or null; mirrors public.inquiry.botid. */
  bot_id: number | null;
  config_snapshot: ConfigSnapshot;
}

export interface CaptureResponse {
  id: number;
  /** ISO-8601 timestamp string. */
  captured_at: string;
  integration_slug: string;
  /** UUID string. */
  egress_correlation_id: string;
  outcome: string;
  /** `{method, url, headers, body}` — see ADR-0004. */
  request: Record<string, unknown>;
  /** `{status, headers, body, duration_ms}`; null on `pre_egress_error`. */
  response: Record<string, unknown> | null;
  /** `{message, type}`; null on `outcome === "success"`. */
  error: Record<string, unknown> | null;
}

export interface InquiryFinalizedResponse {
  /** ISO-8601 timestamp string. */
  captured_at: string;
  external_data: Record<string, unknown>;
  post_creation_processed: boolean;
  handlers_attempted: string[];
  handlers_skipped: Array<Record<string, unknown>>;
}

export interface InquiryJourneyResponse {
  /** Integer; mirrors public.inquiry.id. */
  inquiry_id: number;
  status: InquiryStatus;
  inquiry_created: InquiryCreatedResponse | null;
  captures: CaptureResponse[];
  inquiry_finalized: InquiryFinalizedResponse | null;
  detections: DetectionEventResponse[];
}

export interface InquirySummaryResponse {
  /** Integer; mirrors public.inquiry.id. */
  inquiry_id: number;
  /** ISO-8601 timestamp string. */
  inquiry_created_at: string;
  finalized: boolean;
  detection_count: number;
  max_severity: Severity | null;
}

export interface InquiriesListResponse {
  inquiries: InquirySummaryResponse[];
  next_cursor: string | null;
}

export interface RuleMetadataResponse {
  rule_id: string;
  display_name: string;
  default_severity: Severity;
}

export interface RulesResponse {
  rules: RuleMetadataResponse[];
}

export interface HealthzResponse {
  status: "ok";
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Thrown when a /v1/dashboard/* request returns a non-2xx response.
 * `body` is the parsed JSON body when the response was JSON, otherwise
 * the raw text or `null`.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `API error ${status}`);
    this.name = "ApiError";
  }
}

// ---------------------------------------------------------------------------
// Low-level fetch
// ---------------------------------------------------------------------------

type QueryValue = string | number | boolean | undefined | null;

function toQueryString(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : "";
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.headers ?? {}),
    },
    // `same-origin` is the default and the right policy here: production
    // serves the frontend from the same FastAPI instance, so the browser
    // sends the session cookie automatically.
    credentials: "same-origin",
  });

  if (!response.ok) {
    let body: unknown = null;
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      try {
        body = await response.json();
      } catch {
        // Body wasn't valid JSON despite the content-type — fall back to text.
        body = null;
      }
    } else {
      try {
        body = await response.text();
      } catch {
        body = null;
      }
    }
    throw new ApiError(
      response.status,
      body,
      `${response.status} ${response.statusText}`,
    );
  }

  return (await response.json()) as T;
}

// ---------------------------------------------------------------------------
// Endpoint helpers
// ---------------------------------------------------------------------------

export interface GetDetectionsParams {
  /** Subset of severities to include. Sent as a comma-separated string. */
  severities?: Severity[];
  rule_id?: string;
  since?: Date;
  until?: Date;
  /** 1-500. The server clamps but FastAPI also enforces via 422. */
  limit?: number;
  cursor?: string;
}

export async function getDetections(
  params: GetDetectionsParams = {},
): Promise<DetectionsListResponse> {
  const qs = toQueryString({
    severity:
      params.severities && params.severities.length > 0
        ? params.severities.join(",")
        : undefined,
    rule_id: params.rule_id,
    since: params.since?.toISOString(),
    until: params.until?.toISOString(),
    limit: params.limit,
    cursor: params.cursor,
  });
  return apiFetch<DetectionsListResponse>(`/v1/dashboard/detections${qs}`);
}

export async function getInquiry(
  inquiryId: string,
): Promise<InquiryJourneyResponse> {
  return apiFetch<InquiryJourneyResponse>(
    `/v1/dashboard/inquiries/${encodeURIComponent(inquiryId)}`,
  );
}

export interface GetInquiriesParams {
  since?: Date;
  until?: Date;
  has_detection?: boolean;
  limit?: number;
  cursor?: string;
}

export async function getInquiries(
  params: GetInquiriesParams = {},
): Promise<InquiriesListResponse> {
  const qs = toQueryString({
    since: params.since?.toISOString(),
    until: params.until?.toISOString(),
    has_detection: params.has_detection,
    limit: params.limit,
    cursor: params.cursor,
  });
  return apiFetch<InquiriesListResponse>(`/v1/dashboard/inquiries${qs}`);
}

export async function getRules(): Promise<RulesResponse> {
  return apiFetch<RulesResponse>("/v1/dashboard/rules");
}

export async function getHealthz(): Promise<HealthzResponse> {
  return apiFetch<HealthzResponse>("/v1/dashboard/healthz");
}

export interface SessionStatusResponse {
  authenticated: boolean;
}

export async function logout(): Promise<SessionStatusResponse> {
  return apiFetch<SessionStatusResponse>("/v1/dashboard/auth/logout", {
    method: "POST",
  });
}
